import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import {
  Page,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  Badge,
  Button,
  Spinner,
  Divider,
  Banner,
  IndexTable,
  LegacyCard,
  Modal,
  TextField,
  Select,
} from "@shopify/polaris";
import { formatPrice, formatDate } from "../../utility/funcs";
import { getNextTriggerDate } from "../../utility/InvoicingEngine";
import { addDaysISO } from "../../utility/invoicing";
import { useNavigate } from "react-router-dom";

function AutoInvoicingDashboard() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({});
  const [company, setCompany] = useState({ id: null, name: "" });
  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    // Fetch groups with company info
    const { data: groupsData } = await supabase
      .from("invoicing_groups")
      .select("*, company:company_id(name, rfc)")
      .eq("is_active", true)
      .order("name");

    // For each group, count active reservations and estimate monthly amount
    const enriched = [];
    for (const g of groupsData || []) {
      const { data: reservations } = await supabase
        .from("reservations")
        .select("id, totals, car(plate, model(name))")
        .eq("invoicing_group_id", g.id)
        .eq("status", "active");

      const cars = reservations || [];
      const monthlyEstimate = cars.reduce((sum, r) => sum + (r.totals?.dailyPrice || 0) * 30, 0);

      // Get last invoice run
      const { data: lastRun } = await supabase
        .from("auto_invoice_runs")
        .select("trigger_date, period_to, status")
        .eq("invoicing_group_id", g.id)
        .order("trigger_date", { ascending: false })
        .limit(1);

      const nextTrigger = getNextTriggerDate(g.periodicity, g.weekly_day);

      enriched.push({
        ...g,
        cars,
        carCount: cars.length,
        monthlyEstimate,
        lastRun: lastRun?.[0] || null,
        nextTrigger: nextTrigger.toISOString().substring(0, 10),
      });
    }

    setGroups(enriched);

    // Fetch recent runs
    const { data: runsData } = await supabase
      .from("auto_invoice_runs")
      .select("*, invoicing_group:invoicing_group_id(name), invoice:invoice_id(invoice_number, total)")
      .order("created_at", { ascending: false })
      .limit(50);

    setRuns(runsData || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openNew = () => {
    setForm({
      name: "",
      periodicity: "monthly_1st",
      weekly_day: 1,
      billing_period: "transcurrido",
      credit_enabled: false,
      credit_days: 0,
      auto_mode: "manual",
    });
    setCompany({ id: null, name: "" });
    setCompanySearch("");
    setCompanyResults([]);
    setModalOpen(true);
  };

  const saveGroup = async () => {
    setSaving(true);
    await supabase.from("invoicing_groups").insert({
      name: form.name,
      company_id: company.id || null,
      periodicity: form.periodicity,
      weekly_day: form.periodicity === "weekly" ? form.weekly_day : null,
      billing_period: form.billing_period,
      credit_enabled: form.credit_enabled,
      credit_days: form.credit_enabled ? form.credit_days : 0,
      auto_mode: form.auto_mode,
    });
    setSaving(false);
    setModalOpen(false);
    fetchData();
  };

  if (loading) return <AppWrapper><Page title="Facturación Automática"><Spinner /></Page></AppWrapper>;

  return (
    <AppWrapper>
      <Page
        title="Facturación Automática"
        subtitle="Genera facturas consolidadas por grupo de facturación"
        primaryAction={
          <Button variant="primary" onClick={openNew}>
            Crear Grupo
          </Button>
        }
      >
        <BlockStack gap="400">
          {groups.length === 0 && (
            <Banner>No hay grupos de facturación activos.</Banner>
          )}

          <LegacyCard>
            <IndexTable
              resourceName={{ singular: "Grupo", plural: "Grupos" }}
              itemCount={groups.length}
              selectable={false}
              headings={[
                { title: "Empresa" },
                { title: "Proyecto" },
                { title: "Autos" },
                { title: "Estimado Mensual" },
                { title: "Última Factura" },
                { title: "Próximo" },
              ]}
            >
              {groups.map((g, i) => (
                <IndexTable.Row
                  key={g.id}
                  id={g.id}
                  position={i}
                  onClick={() => navigate(`/invoicing/groups/${g.id}`)}
                >
                  <IndexTable.Cell>
                    <Text variant="bodySm">{g.company?.name || "-"}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm" fontWeight="semibold">{g.name}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm">{g.carCount}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm">{formatPrice(g.monthlyEstimate)}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm" tone="subdued">
                      {g.lastRun ? formatDate(g.lastRun.trigger_date) : "-"}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm">{formatDate(g.nextTrigger)}</Text>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>

          {runs.length > 0 && (
            <>
              <Text variant="headingMd">Historial</Text>
              <LegacyCard>
                <IndexTable
                  resourceName={{ singular: "Factura", plural: "Facturas" }}
                  itemCount={runs.length}
                  selectable={false}
                  headings={[
                    { title: "Grupo" },
                    { title: "Periodo" },
                    { title: "Factura" },
                    { title: "Estado" },
                    { title: "Fecha" },
                  ]}
                >
                  {runs.map((r, i) => (
                    <IndexTable.Row key={r.id} id={r.id} position={i}>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{r.invoicing_group?.name || "-"}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {/* period_to is the last billed day; show the return day (+1) */}
                        <Text variant="bodySm">{r.period_from} → {r.period_to ? addDaysISO(r.period_to, 1) : "-"}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{r.invoice?.invoice_number || "-"}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge size="small" tone={r.status === "stamped" ? "success" : r.status === "error" ? "critical" : "attention"}>
                          {r.status}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{formatDate(r.created_at?.substring(0, 10))}</Text>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </LegacyCard>
            </>
          )}
        </BlockStack>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Crear Grupo de Facturación"
          primaryAction={{
            content: "Guardar",
            onAction: saveGroup,
            loading: saving,
            disabled: !form.name?.trim(),
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setModalOpen(false) }]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <TextField
                label="Nombre del Grupo"
                value={form.name || ""}
                onChange={(val) => setForm({ ...form, name: val })}
                placeholder="Ej: Proyecto Norte, Obra Monterrey"
                autoComplete="off"
              />
              <TextField
                label="Empresa"
                value={company.id ? company.name : companySearch}
                onChange={async (val) => {
                  setCompanySearch(val);
                  setCompany({ id: null, name: "" });
                  if (val.trim().length < 2) { setCompanyResults([]); return; }
                  const { data } = await supabase
                    .from("companies")
                    .select("id, name")
                    .ilike("name", `%${val.trim()}%`)
                    .limit(5);
                  setCompanyResults(data || []);
                }}
                placeholder="Buscar empresa..."
                autoComplete="off"
              />
              {companyResults.length > 0 && !company.id && (
                <BlockStack gap="050">
                  {companyResults.map((c) => (
                    <Button key={c.id} variant="plain" onClick={() => {
                      setCompany({ id: c.id, name: c.name });
                      setCompanySearch("");
                      setCompanyResults([]);
                    }}>
                      {c.name}
                    </Button>
                  ))}
                </BlockStack>
              )}
              <Select
                label="Periodicidad"
                value={form.periodicity}
                options={[
                  { label: "1ero de cada mes", value: "monthly_1st" },
                  { label: "Último de cada mes", value: "monthly_last" },
                  { label: "Semanal", value: "weekly" },
                  { label: "Quincenal", value: "biweekly" },
                ]}
                onChange={(val) => setForm({ ...form, periodicity: val })}
              />
              {form.periodicity === "weekly" && (
                <Select
                  label="Día de la semana"
                  value={String(form.weekly_day)}
                  options={[
                    { label: "Lunes", value: "1" },
                    { label: "Martes", value: "2" },
                    { label: "Miércoles", value: "3" },
                    { label: "Jueves", value: "4" },
                    { label: "Viernes", value: "5" },
                    { label: "Sábado", value: "6" },
                    { label: "Domingo", value: "0" },
                  ]}
                  onChange={(val) => setForm({ ...form, weekly_day: parseInt(val) })}
                />
              )}
              <Select
                label="Periodo a facturar"
                value={form.billing_period}
                options={[
                  { label: "Transcurrido (uso pasado)", value: "transcurrido" },
                  { label: "Posterior (uso futuro)", value: "posterior", disabled: !form.credit_enabled },
                ]}
                onChange={(val) => setForm({ ...form, billing_period: val })}
              />
              <Select
                label="Crédito"
                value={form.credit_enabled ? "yes" : "no"}
                options={[
                  { label: "Sin crédito", value: "no" },
                  { label: "Con crédito", value: "yes" },
                ]}
                onChange={(val) => {
                  const enabled = val === "yes";
                  setForm({ ...form, credit_enabled: enabled, billing_period: !enabled && form.billing_period === "posterior" ? "transcurrido" : form.billing_period });
                }}
              />
              {form.credit_enabled && (
                <TextField
                  label="Días de crédito"
                  type="number"
                  value={String(form.credit_days || 0)}
                  onChange={(val) => setForm({ ...form, credit_days: parseInt(val) || 0 })}
                  suffix="días"
                />
              )}
              <Select
                label="Modo de facturación"
                value={form.auto_mode}
                options={[
                  { label: "Manual", value: "manual" },
                  { label: "Auto Borrador", value: "auto_draft" },
                  { label: "Auto Timbrar", value: "auto_stamp" },
                ]}
                onChange={(val) => setForm({ ...form, auto_mode: val })}
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default AutoInvoicingDashboard;
